/** Same key as ops-core so preference survives across apps on one origin. */
export const UI_MODE_STORAGE_KEY = 'intelaccounts_ui_mode';

export type UiModePreference = 'modern' | 'classic';

const PUBLIC_PATH_PREFIXES = ['/login', '/my-companies', '/login-v2'];

/**
 * Routes implemented in the modern ops-core SPA (subset of tya-operation).
 * Used so "Modern UI" / href helpers do not send users to ops-core with a path it cannot render.
 */
const OPS_CORE_ROUTE_PREFIXES = [
  '/raisepr',
  '/raise-draft-pr',
  '/update-pr',
  '/view-pr',
  '/view-approval',
  '/rejected-pr',
  '/allpr-approvals',
  '/all-pr-approval',
  '/draft-pr',
  '/hold-pr-for-additional-info',
  '/close-pr',
  '/pr-approved-by-me',
  '/prapproval-summary',
  '/my-purchases-pr',
  '/accepted-items',
  '/return-to-store-acceptance',

  /** Invoice module (app-routing.module.ts) — required or menu/API redirects leave ops-core → blank classic shell. */
  '/add-ddc-invoice-payments',
  '/add-invoice-payments',
  '/add-invoice-v2',
  '/add-invoice-with-po-advance-tds',
  '/addinvoice',
  '/all-approved-invoices',
  '/all-hold-invoice',
  '/all-invoices',
  '/all-recurring-inv',
  '/approved-generate-invoice-android-summary',
  '/approved-summary-recurring-inv',
  '/auto-generated-inv',
  '/completed-invoice-payment',
  '/copy-new-direct-invoice',
  '/create-recurring-inv',
  '/debit-credit-by-vendor',
  '/direct-invoice',
  '/direct-invoices',
  '/draft-invoice',
  '/draft-recurring-inv',
  '/generate-invoice-android',
  '/generate-invoice-android-approval',
  '/grn-invoice-file',
  '/grn-invoice-upload-summary',
  '/hold-invoice-for-additional-info',
  '/hold-invoice-payment',
  '/hold-invoice-payment-for-additional-info',
  '/hold-vendor-invoices',
  '/invoice-approval',
  '/invoice-dashboard',
  '/invoice-data',
  '/invoice-debit-note',
  '/invoice-history',
  '/invoice-my-approved-summary',
  '/invoice-payment-approval',
  '/invoice-payments',
  '/invoice-summary',
  '/invoices',
  '/mailed-invoice',
  '/mailed-invoice-cc',
  '/multi-grn-invoice',
  '/my-approved-invoice-summary',
  '/my-generate-invoice-android',
  '/my-invoices',
  '/my-invoices-summary',
  '/my-mailed-invoice',
  '/po-advance-payment',
  '/raise-direct-invoice',
  '/raise-draft-direct-invoice',
  '/raise-recurring-direct-invoice',
  '/raise-rejected-inv',
  '/raise-rejected-invoice',
  '/recurring-inv-for-approval',
  '/rejected-invoice',
  '/rejected-vendor-invoice',
  '/rejected-vendor-invoice-dashboard',
  '/removed-mailed-invoice',
  '/removed-my-mailed-invoice',
  '/scan-invoice',
  '/test-direct-invoice',
  '/update-invoice',
  '/update-recurring-inv',
  '/vendor-add-invoice',
  '/vendor-direct-invoice',
  '/vendor-invoice-summary',
  '/vendor-my-invoice',
  '/view-invoice',
  '/zero-touch-invoice',
  '/new-irn',
  '/new-srn',
  '/myirn-summary',
  '/all-open-irn',
  '/add-grn',
  '/grn-summary',
  '/grn-invoice-upload-summary',
  '/allocate-grn-rack',
  '/allocated-grn-rack',
  '/hold-grn',
  '/my-grn-excess',
  '/grn-excess-for-approvals',
  '/grn-excess-approvals-summary',
  '/material-readiness-po',
  '/po-modification-request'
];

export function readUiModePreference(): UiModePreference {
  if (typeof localStorage === 'undefined') {
    return 'classic';
  }
  const stored = localStorage.getItem(UI_MODE_STORAGE_KEY);
  const v = String(stored != null ? stored : '').toLowerCase();
  return v === 'modern' ? 'modern' : 'classic';
}

export function writeUiModePreference(mode: UiModePreference): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(UI_MODE_STORAGE_KEY, mode);
}

export function pathHasOpsCorePrefix(path: string): boolean {
  const p = (path || '').trim();
  return p === '/ops-core' || p.startsWith('/ops-core/');
}

/**
 * Modern QA/prod can serve app under pathname `/ops-core` while hash is flat (`#/raisepr`).
 */
export function isOpsCorePathnameShell(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const pathname = (window.location.pathname || '').replace(/\/+$/, '');
  return pathname === '/ops-core' || pathname.startsWith('/ops-core/');
}

/**
 * Build hash URL while preserving `/ops-core` pathname shell for modern mode.
 * Set `useRootPathname` for forcing classic root shell.
 */
export function hrefForAppHashPath(path: string, options?: { useRootPathname?: boolean }): string {
  if (typeof window === 'undefined') {
    const p = (path || '/').trim() || '/';
    return p.startsWith('/') ? p : `/${p}`;
  }
  const raw = (path || '/').trim() || '/';
  const qIndex = raw.indexOf('?');
  const pathPart = (qIndex >= 0 ? raw.slice(0, qIndex) : raw).trim();
  const qs = qIndex >= 0 ? raw.slice(qIndex) : '';
  const p = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  const logicalPath = stripOpsCoreFromPath(p.split('?')[0] || '/');
  const pathForHash =
    logicalPath === '/' || logicalPath === '/ops-core' ? '/dashboard' : logicalPath;
  const hashBody =
    pathForHash.replace(/^\//, '').replace(/^\/+/, '') || 'dashboard';
  const useRoot = options && options.useRootPathname === true;
  // Only keep `/ops-core` pathname for routes the modern SPA actually implements (parity with ops-core).
  if (!useRoot && isOpsCorePathnameShell() && isOpsCoreSupportedRoute(logicalPath)) {
    return `${window.location.origin}/ops-core/#/${hashBody}${qs}`;
  }
  return `${window.location.origin}/#/${hashBody}${qs}`;
}

export function stripOpsCoreFromPath(path: string): string {
  const p = (path || '').trim() || '/';
  if (p === '/ops-core') {
    return '/dashboard';
  }
  if (p.startsWith('/ops-core/')) {
    const stripped = '/' + p.slice('/ops-core/'.length);
    return stripped === '/' ? '/dashboard' : stripped;
  }
  return p.startsWith('/') ? p : '/' + p;
}

/** Whether a logical hash path exists in the ops-core router (not necessarily in tya-operation). */
export function isOpsCoreSupportedRoute(path: string): boolean {
  const logical = stripOpsCoreFromPath((path || '/').split('?')[0] || '/');
  if (logical === '/' || logical === '/ops-core') {
    return true;
  }
  return OPS_CORE_ROUTE_PREFIXES.some(
    (prefix) => logical === prefix || logical.startsWith(prefix + '/')
  );
}
/** Show Classic / Modern toggle only on routes listed in OPS_CORE_ROUTE_PREFIXES. */
export function shouldShowUiModeToggle(path: string): boolean {
  if (isPublicAuthBypassPath(path)) {
    return false;
  }
  return isOpsCoreSupportedRoute(path);
}

export function isPublicAuthBypassPath(path: string): boolean {
  const p = stripOpsCoreFromPath(path.split('?')[0] || '/');
  return PUBLIC_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

/** Use after login / company pick / auth failures so preference is respected. */
export function dashboardPathForAuthRedirect(): string {
  if (isOpsCorePathnameShell()) {
    return '/dashboard';
  }
  return readUiModePreference() === 'modern' ? '/ops-core/dashboard' : '/dashboard';
}

export function prefixedPathForModern(logicalPath: string): string {
  const logical = stripOpsCoreFromPath((logicalPath.split('?')[0] || '/').trim());
  if (logical === '/' || logical === '/dashboard') {
    return '/ops-core/dashboard';
  }
  return '/ops-core' + logical;
}

export function applyUiModeToRedirectPath(url: string): string {
  const raw = String(url != null ? url : '').trim();
  if (!raw) {
    if (isOpsCorePathnameShell()) {
      return '/dashboard';
    }
    return readUiModePreference() === 'modern' ? '/ops-core/dashboard' : '/dashboard';
  }
  const qIndex = raw.indexOf('?');
  const pathPart = (qIndex >= 0 ? raw.slice(0, qIndex) : raw).trim();
  const qs = qIndex >= 0 ? raw.slice(qIndex) : '';
  const normalized = pathPart.charAt(0) === '/' ? pathPart : '/' + pathPart;
  if (isPublicAuthBypassPath(normalized)) {
    return normalized + qs;
  }
  const logical = stripOpsCoreFromPath(normalized);
  // In `/ops-core` pathname shell, router uses logical hash paths only.
  if (isOpsCorePathnameShell()) {
    return logical + qs;
  }
  // Explicit ops-core routes always stay modern.
  if (pathHasOpsCorePrefix(normalized)) {
    return prefixedPathForModern(logical) + qs;
  }
  // Non-prefixed routes stay in classic shell.
  return logical + qs;
}



// WEBPACK FOOTER //
// /codebuild/output/src1294910137/src/src/app/shared/utils/ui-mode.ts